/**
 * @file ShippingMethodSection.tsx
 *
 * Manages one method type (flat-rate OR free-shipping) inside the custom-shipping
 * form: a reorderable {@link DraggableDataTable} of that type's methods (an
 * `enabled` toggle + name, with edit/delete), and below it either an "Add option"
 * link or the inline add/edit sub-form ({@link FlatRateForm} / {@link
 * FreeShippingForm}). Method data lives in the parent form hook; this component
 * reports add/update/remove/reorder and owns the transient "sub-form open" state
 * (surfaced to the parent via `onEditingChange` so it can disable the zone footer
 * while a method is being added/edited). Mirrors WSB-v2's per-type shipping-method
 * sections.
 */

import { useState } from 'react';
import { Action, Icon, Toggle } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { DraggableDataTable, type DraggableDataTableColumn } from '../../components/DraggableDataTable';
import { FlatRateForm } from './FlatRateForm';
import { FreeShippingForm } from './FreeShippingForm';
import { createMethodOptionId } from './custom-shipping-form-utils';
import { methodTypeLabel } from './shipping-method-labels';
import type {
  FlatRateData,
  FreeShippingData,
  MethodFormOption,
  ShippingMethodType,
} from './custom-shipping-form-types';

interface ShippingMethodSectionProps {
  methodType: ShippingMethodType;
  /** The methods of this type, in display order. */
  methods: MethodFormOption[];
  currencySymbol: string;
  /** `data-testid` namespace for this section's table/rows/actions. */
  testIdPrefix: string;
  dragHandleAriaLabel: string;
  onAdd: (option: MethodFormOption) => void;
  onUpdate: (id: string, option: MethodFormOption) => void;
  onRemove: (id: string) => void;
  /** Receives the next ordered array of this type's methods after a drag. */
  onReorder: (reordered: MethodFormOption[]) => void;
  /** Notifies the parent whether the inline add/edit sub-form is open. */
  onEditingChange?: (open: boolean) => void;
}

type Editing = { mode: 'add' } | { mode: 'edit'; option: MethodFormOption };

/** A table row carrying the original method option for the actions/toggle. */
interface MethodRow {
  id: string;
  enabled: boolean;
  name: string;
  option: MethodFormOption;
}

/** Returns a copy of the method with its `enabled` flag set, preserving its type. */
function withEnabled(option: MethodFormOption, enabled: boolean): MethodFormOption {
  if (option.methodType === 'flat_rate') {
    return { ...option, data: { ...option.data, enabled } };
  }
  return { ...option, data: { ...option.data, enabled } };
}

/**
 * Renders one method type's table and its inline add/edit sub-form.
 *
 * @param props - The method type, its methods, currency, test-id prefix, and the
 *   add/update/remove/reorder handlers.
 *
 * @returns The method-section element.
 */
export function ShippingMethodSection({
  methodType,
  methods,
  currencySymbol,
  testIdPrefix,
  dragHandleAriaLabel,
  onAdd,
  onUpdate,
  onRemove,
  onReorder,
  onEditingChange,
}: ShippingMethodSectionProps) {
  const intl = useIntl();
  const [editing, setEditing] = useState<Editing | null>(null);

  const openEditing = (next: Editing): void => {
    setEditing(next);
    onEditingChange?.(true);
  };

  const closeEditing = (): void => {
    setEditing(null);
    onEditingChange?.(false);
  };

  const rows: MethodRow[] = methods.map((option) => ({
    id: option.id,
    enabled: option.data.enabled,
    name: option.data.title || intl.formatMessage(methodTypeLabel(option.methodType)),
    option,
  }));

  const columns: Array<DraggableDataTableColumn<MethodRow>> = [
    {
      enabled: {
        label: '',
        shrink: true,
        renderCell: (row) => (
          <Toggle
            condensed
            checked={row.enabled}
            onChange={() => onUpdate(row.option.id, withEnabled(row.option, !row.enabled))}
          />
        ),
      },
    },
    { name: { label: intl.formatMessage(adminMessages.shippingFieldName) } },
  ];

  const commitFlatRate = (data: FlatRateData): void => {
    if (!editing) {
      return;
    }
    if (editing.mode === 'add') {
      onAdd({ id: createMethodOptionId('flat_rate'), methodType: 'flat_rate', data });
    } else {
      onUpdate(editing.option.id, {
        id: editing.option.id,
        instanceId: editing.option.instanceId,
        methodType: 'flat_rate',
        data,
      });
    }
    closeEditing();
  };

  const commitFreeShipping = (data: FreeShippingData): void => {
    if (!editing) {
      return;
    }
    if (editing.mode === 'add') {
      onAdd({ id: createMethodOptionId('free_shipping'), methodType: 'free_shipping', data });
    } else {
      onUpdate(editing.option.id, {
        id: editing.option.id,
        instanceId: editing.option.instanceId,
        methodType: 'free_shipping',
        data,
      });
    }
    closeEditing();
  };

  return (
    <div className="gv-flex gv-flex-col gv-gap-md">
      {rows.length > 0 && (
        <DraggableDataTable<MethodRow>
          testIdPrefix={testIdPrefix}
          headerColumns={columns}
          rows={rows}
          dragHandleAriaLabel={dragHandleAriaLabel}
          getPreviewLabel={(row) => row.name}
          onReorder={(reordered) => onReorder(reordered.map((row) => row.option))}
          actions={[
            {
              icon: 'edit',
              label: intl.formatMessage(adminMessages.shippingEditMethod),
              testId: `${testIdPrefix}-edit`,
              onAction: (row) => openEditing({ mode: 'edit', option: row.option }),
            },
            {
              icon: 'delete',
              label: intl.formatMessage(adminMessages.shippingDeleteMethod),
              testId: `${testIdPrefix}-delete`,
              onAction: (row) => onRemove(row.option.id),
            },
          ]}
        />
      )}

      {editing ? (
        methodType === 'flat_rate' ? (
          <FlatRateForm
            prev={
              editing.mode === 'edit' && editing.option.methodType === 'flat_rate'
                ? editing.option.data
                : undefined
            }
            currencySymbol={currencySymbol}
            showDivider={methods.length > 0}
            onSubmit={commitFlatRate}
            onCancel={closeEditing}
          />
        ) : (
          <FreeShippingForm
            prev={
              editing.mode === 'edit' && editing.option.methodType === 'free_shipping'
                ? editing.option.data
                : undefined
            }
            currencySymbol={currencySymbol}
            showDivider={methods.length > 0}
            onSubmit={commitFreeShipping}
            onCancel={closeEditing}
          />
        )
      ) : (
        <Action
          component="button"
          type="button"
          data-testid={`${testIdPrefix}-add`}
          onClick={() => openEditing({ mode: 'add' })}
        >
          <Icon name="add" size="small" />
          <span className="gv-text-sm gv-text-bold">
            <FormattedMessage {...adminMessages.shippingAddOption} />
          </span>
        </Action>
      )}
    </div>
  );
}
